import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { invoicesService } from '../services/api';
import { fmtDate } from '../utils/formatters';
import { colunaPagamento, PagamentoDaFatura as Pagamento, resumoDosPagamentos, rotuloDaOrigem } from '../utils/pagamentosDaFatura';

/**
 * Célula da coluna Pagamento: data do ÚLTIMO pagamento, quantos houve, e o
 * relógio que abre a lista (valor, data e origem de cada um). Ver
 * `utils/pagamentosDaFatura.ts` para o porquê.
 *
 * A lista vem de `GET /invoices/:id/payments` só quando alguém clica: buscar
 * para cada linha ao carregar seria uma chamada por fatura na tela inteira.
 * Guardada por fatura E pelo valor pago, para um pagamento novo não reabrir a
 * lista velha.
 */

const cache = new Map<string, Pagamento[]>();

const brl = (v: any) =>
  (Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface Props {
  inv: {
    id: number;
    code?: string;
    paymentDate?: string | null;
    lastPaymentDate?: string | null;
    paymentsCount?: number | null;
    paidValue?: number | string | null;
    totalValue?: number | string | null;
    jurosMulta?: number | string | null;
  };
}

export default function PagamentoDaFatura({ inv }: Props) {
  const chave = `${inv.id}:${Number(inv.paidValue) || 0}`;
  const [lista, setLista] = useState<Pagamento[] | null>(() => cache.get(chave) ?? null);
  const [aberto, setAberto] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const botaoRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLista(cache.get(chave) ?? null);
  }, [chave]);

  // O popover vai para o <body> com posição fixa: o contêiner da tabela tem
  // overflow escondido e, com poucas linhas, cortava a lista a uma faixa de
  // poucos pixels sob a linha. Rolar a página fecha, em vez de deixar o popover
  // flutuando longe da linha a que pertence.
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => {
      const alvo = e.target as Node;
      if (popRef.current?.contains(alvo) || botaoRef.current?.contains(alvo)) return;
      setAberto(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); setAberto(false); } // o Esc é do popover: a casca de fora não fecha junto
    };
    const fechar = () => setAberto(false);
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    window.addEventListener('scroll', fechar, true);
    window.addEventListener('resize', fechar);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', esc);
      window.removeEventListener('scroll', fechar, true);
      window.removeEventListener('resize', fechar);
    };
  }, [aberto]);

  const { data, quantidade } = colunaPagamento(inv, lista);
  // Tem pagamento registrado? O valor pago vem do trigger que soma a tabela de
  // pagamentos, então é o sinal confiável mesmo quando a data se perdeu.
  // Com a contagem do backend, ela decide; o paymentDate só vale sem ela.
  const temPagamento = (Number(inv.paidValue) || 0) > 0 || (quantidade != null ? quantidade > 0 : !!inv.paymentDate);

  const abrir = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (aberto) { setAberto(false); return; }
    const r = botaoRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
    setAberto(true);
    if (lista) return;
    setCarregando(true);
    setErro(false);
    try {
      const r = await invoicesService.getPayments(inv.id);
      const l: Pagamento[] = Array.isArray(r) ? r : [];
      cache.set(chave, l);
      setLista(l);
    } catch {
      setErro(true);
    } finally {
      setCarregando(false);
    }
  };

  const resumo = lista ? resumoDosPagamentos(lista) : null;
  const juros = Number(inv.jurosMulta) || 0;

  return (
    <div className="inline-flex flex-col items-center">
      {/* ⛔ A primeira linha leva SÓ a data e o relógio (96 px medidos, na coluna de 96 px úteis). Até
          07/10/2026 o juros ia nela: 168 px que entravam na coluna Ações, e o clique no relógio caía em
          "Baixar PDF". Qualquer coisa nova vai numa linha de baixo, como o juros e a contagem. */}
      <div className="flex items-center gap-1">
        {data ? <span className="text-slate-300">{fmtDate(data)}</span> : <span className="text-slate-600">—</span>}
        {temPagamento && (
          <button
            ref={botaoRef}
            type="button"
            onClick={abrir}
            className="p-0.5 text-slate-500 hover:text-emerald-400 rounded"
            title="Ver os pagamentos desta fatura"
            aria-expanded={aberto}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        )}
      </div>
      {juros > 0 && (
        <span className="text-[11px] text-amber-400 leading-tight whitespace-nowrap" title={`R$ ${brl(juros)} de juros e multa`}>
          +R$ {brl(juros)}
        </span>
      )}
      {quantidade != null && quantidade > 1 && (
        <span className="text-[10px] text-slate-400 leading-tight">{quantidade} pagamentos</span>
      )}

      {aberto && pos && createPortal(
        <div
          ref={popRef}
          style={{ position: 'fixed', top: pos.top, right: pos.right }}
          className="z-[60] w-[300px] max-w-[calc(100vw-16px)] bg-slate-800 border border-slate-600 rounded-xl shadow-2xl p-3 text-left whitespace-normal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-xs font-semibold text-white pb-2 border-b border-slate-700">
            Pagamentos{inv.code ? ` · ${inv.code}` : ''}
          </div>
          {carregando ? (
            <div className="text-slate-400 text-xs text-center py-3">Carregando…</div>
          ) : erro ? (
            <div className="text-red-400 text-xs text-center py-3">Não consegui buscar os pagamentos. Tente de novo.</div>
          ) : !lista || lista.length === 0 ? (
            <div className="text-slate-400 text-xs text-center py-3">Nenhum pagamento registrado.</div>
          ) : (
            <>
              <ul className="divide-y divide-slate-700/60">
                {lista.map((p, i) => (
                  <li key={p.id ?? i} className="py-1.5 flex items-start justify-between gap-3 text-xs">
                    <div className="min-w-0">
                      <div className="text-slate-200">{p.paymentDate ? fmtDate(p.paymentDate) : 'sem data'}</div>
                      <div className="text-[10px] text-slate-500">{rotuloDaOrigem(p.source)}</div>
                    </div>
                    <span className="text-emerald-400 font-medium tabular-nums whitespace-nowrap">R$ {brl(p.amount)}</span>
                  </li>
                ))}
              </ul>
              {resumo && (
                <div className="pt-2 mt-1 border-t border-slate-700 flex justify-between text-xs">
                  <span className="text-slate-400">Pago</span>
                  <span className="text-white tabular-nums">
                    R$ {brl(resumo.total)}
                    {Number(inv.totalValue) > 0 && <span className="text-slate-400"> de R$ {brl(inv.totalValue)}</span>}
                  </span>
                </div>
              )}
            </>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
